import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import Switch from '@mui/material/Switch';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { 
    fetchFishbowlData, 
    configureFishbowlStore, 
    verifyFishbowlApproval,
    resetFishbowlConfigureState 
} from './fishbowlSlice';

const steps = [
    'Connection Settings',
    'Authentication Details',
    'Carrier Setup',
    'Administrative Approval',
    'Success Confirmation'
];

const FishbowlIntegration = () => {
    const dispatch = useDispatch();

    // Redux Global Selectors
    const fishbowlProgress = useSelector(state => state.fishbowl.progress);
    const fishbowlConfigure = useSelector(state => state.fishbowl.configure);
    const savedConfig = fishbowlProgress?.data;

    // Local Input Form Hooks
    const [endpointUrl, setEndpointUrl] = useState('');
    const [portNumber, setPortNumber] = useState('4131');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [carrierId, setCarrierId] = useState('');

    // Shipment Update Configuration State Hooks
    const [updateFlag, setUpdateFlag] = useState(false);
    const [onCreateFlag, setOnCreateFlag] = useState(false);
    const [onCloseFlag, setOnCloseFlag] = useState(true);

    // Workflow Routing Controls
    const [activeStep, setActiveStep] = useState(0);
    const [errorMsg, setErrorMsg] = useState('');
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    
    // --- FIXED: EDITING ISOLATION GUARD ---
    // This tracks whether the user is in an active edit cycle to prevent old DB configurations from forcing step 4
    const [isEditing, setIsEditing] = useState(true);

    // Load active settings profile state immediately on mount
    useEffect(() => {
        dispatch(fetchFishbowlData());
        window.isFishbowlManualVerifyAction = false;
        setIsEditing(true); // Force edit mode tracking on mount
    }, [dispatch]);

    // Handle persistent data mapping from DB payload shifts
    useEffect(() => {
        if (savedConfig) {
            setEndpointUrl(savedConfig.endpoint_url || '');
            setPortNumber(savedConfig.port_number || '4131');
            setUsername(savedConfig.username || '');
            setPassword(savedConfig.password || '');
            setCarrierId(savedConfig.carrier_id || '');
            
            setUpdateFlag(savedConfig.is_update_shipment ?? false);
            setOnCreateFlag(savedConfig.is_update_shipment_on_create ?? false);
            setOnCloseFlag(savedConfig.is_update_shipment_on_close ?? true);
            
            // On click edit or initial drawer open, we ALWAYS enforce Step 0
            if (isInitialLoad) {
                setActiveStep(0);
                setIsInitialLoad(false);
            }
        }
    }, [savedConfig, isInitialLoad]);

    // Track Redux Configuration updates and handle seamless forward transitions
    useEffect(() => {
        if (fishbowlConfigure?.message) {
            setErrorMsg(fishbowlConfigure.message);
            
            if (!fishbowlConfigure.hasErrors) {
                if (activeStep === 2) {
                    setActiveStep(3); // Connection successful, advance to Administrative Approval block
                } else {
                    setActiveStep((prev) => prev + 1); 
                }
            } else if (activeStep === 2) {
                if (fishbowlConfigure.status === 401) {
                    setActiveStep(3);
                }
            }
            
            dispatch(resetFishbowlConfigureState());
        }
    }, [fishbowlConfigure, activeStep, dispatch]);

    // --- FIXED SUCCESS ROUTING TRANSITION LOOP ---
    useEffect(() => {
        // Only jump to step 4 automatically if we are NOT actively editing/reconfiguring parameters
        if (!isEditing && (fishbowlProgress?.isApproved === true || (savedConfig && parseInt(savedConfig.current_step) === 4))) {
            setActiveStep(4);
        }
    }, [fishbowlProgress, savedConfig, isEditing]);

    // --- DECISIVE STEP-BY-STEP STEP VALIDATION ---
    const isCurrentStepInvalid = () => {
        if (activeStep === 0) return endpointUrl.trim() === '' || portNumber.trim() === '';
        if (activeStep === 1) return username.trim() === ''; 
        if (activeStep === 2) return carrierId.trim() === '';
        return false;
    };

    const handleNext = () => {
        setErrorMsg('');

        const payload = {
            current_step: activeStep,
            endpoint_url: endpointUrl,
            port_number: portNumber,
            username: username,
            password: password,
            carrier_id: carrierId
        };

        dispatch(configureFishbowlStore(payload));
    };

    const handleBack = () => {
        setErrorMsg('');
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleVerifyApproval = () => {
        setErrorMsg('');
        window.isFishbowlManualVerifyAction = true;
        
        // Turn off editing block lockouts since they have actively requested validation checking
        setIsEditing(false); 

        // Package shipment synchronization flags during final step verification
        const payload = {
            current_step: activeStep,
            endpoint_url: endpointUrl,
            port_number: portNumber,
            username: username,
            password: password,
            carrier_id: carrierId,
            is_update_shipment: updateFlag,
            is_update_shipment_on_create: onCreateFlag,
            is_update_shipment_on_close: onCloseFlag
        };

        dispatch(configureFishbowlStore(payload));
    };

    const handleShipmentConfigChange = (event, type) => {
        if (type === 'switch') {
            setUpdateFlag(event.target.checked);
        } else if (type === 'radio') {
            const isCreate = event.target.value === 'create_shipment';
            setOnCreateFlag(isCreate);
            setOnCloseFlag(!isCreate);
        }
    };

    const isComponentWorking = fishbowlProgress?.loading || fishbowlConfigure?.loading;

    return (
        <Box sx={{ width: '100%', p: 3, maxWidth: 900, mx: 'auto' }}>
            <Card variant="outlined" sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <CardHeader 
                    title={<Typography variant="h6" sx={{ fontWeight: 800 }}>Fishbowl Inventory Integration</Typography>}
                    subheader="Follow the multi-step configuration process below to synchronize order management workflows."
                    sx={{ bgcolor: '#f8f9fa', borderBottom: '1px solid #e5e7eb', py: 2.5, px: 3 }}
                />
                <Divider />
                <CardContent sx={{ p: 4 }}>
                    
                    <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 5 }}>
                        {steps.map((label, index) => (
                            <Step key={label}>
                                <StepLabel
                                    StepIconProps={{
                                        sx: {
                                            '&.Mui-active': { color: '#212b36' },
                                            '&.Mui-completed': { color: '#22c55e' }
                                        }
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: activeStep === index ? 700 : 500, color: activeStep === index ? '#161c24' : '#637381' }}>
                                        {label}
                                    </Typography>
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    <Box sx={{ minHeight: '220px', mb: 4 }}>
                        {activeStep === 0 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: '8px', mb: 1 }}>
                                        Provide the target hostname environment URL routing and designated access port metrics.
                                    </Alert>
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', mb: 0.5, display: 'block' }}>
                                        ENDPOINT URL
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        disabled={isComponentWorking}
                                        placeholder="e.g. http://xyz.fishbowl.com"
                                        value={endpointUrl}
                                        onChange={(e) => setEndpointUrl(e.target.value)}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', mb: 0.5, display: 'block' }}>
                                        PORT NUMBER
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        disabled={isComponentWorking}
                                        placeholder="4131"
                                        value={portNumber}
                                        onChange={(e) => setPortNumber(e.target.value)}
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>
                        )}

                        {activeStep === 1 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: '8px', mb: 1 }}>
                                        Input the API user authorization credentials matching your system specifications.
                                    </Alert>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', mb: 0.5, display: 'block' }}>
                                        USERNAME
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        disabled={isComponentWorking}
                                        placeholder="e.g. xyz"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', mb: 0.5, display: 'block' }}>
                                        PASSWORD
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="password"
                                        disabled={isComponentWorking}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>
                        )}

                        {activeStep === 2 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Box sx={{ p: 2.5, bgcolor: '#fff9f0', borderRadius: '8px', border: '1px solid #ffecb3', mb: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#b78103', display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <InfoIcon sx={{ fontSize: 18 }} /> Mandatory Action Required in Fishbowl Client Dashboard
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#5c4308', pl: 3.25, lineHeight: 1.6 }}>
                                            1. Log into your Fishbowl client program platform dashboard.<br />
                                            2. Navigate to <b>Admin &gt; Shipping Carriers</b> settings configuration panel.<br />
                                            3. Create a brand new carrier profile named explicitly as <b>"ShippingChimp"</b>.<br />
                                            4. Extract the newly auto-generated internal numeric Carrier Database ID value and enter it below.
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', mb: 0.5, display: 'block' }}>
                                        INTERNAL CARRIER ID VALUE
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        disabled={isComponentWorking}
                                        placeholder="e.g. 20"
                                        value={carrierId}
                                        onChange={(e) => setCarrierId(e.target.value)}
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>
                        )}

                        {activeStep === 3 && (
                            <Box>
                               <Alert 
                                    severity="warning" 
                                    sx={{ 
                                        borderRadius: '8px', 
                                        mb: 3,
                                        bgcolor: '#212b36', 
                                        '& p, & div, & span': {
                                            color: '#ffffff !important'
                                        }
                                    }}
                                >
                                    <p style={{ margin: '0 0 6px 0', fontWeight: 700, fontSize: '14px' }}>
                                        Awaiting Administrator Permission Authorization
                                    </p>
                                    <p style={{ margin: 0, fontSize: '13px', opacity: 0.95 }}>
                                        {errorMsg || "A new integrated application has been added to Fishbowl. Please contact the Fishbowl administrator to approve this integrated application."}
                                    </p>
                                </Alert>
                                
                                <Box sx={{ p: 3, border: '1px dashed #b2b2b2', borderRadius: '8px', bgcolor: '#fafafa', mb: 3 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#212b36' }}>
                                        To Approve This Application:
                                    </Typography>
                                    <Typography variant="body2" component="div" sx={{ color: '#637381', pl: 1, '& p': { my: 0.75 } }}>
                                        <p>1. Open the primary Fishbowl Server management console window frame platform.</p>
                                        <p>2. Go to the <b>Integrated Applications</b> module context tab selection list.</p>
                                        <p>3. Find the profile titled <b>"ShippingChimp"</b> and click on <b>Approve / Grant Access</b>.</p>
                                        <p>4. Once approved inside your dashboard, click the verification button below to activate synchronization streams.</p>
                                    </Typography>
                                </Box>

                                <Card variant="outlined" sx={{ borderRadius: '8px', border: '1px solid #e5e7eb', bgcolor: '#fcfcfd', mb: 3 }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <LocalShippingIcon sx={{ color: '#637381' }} />
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#212b36' }}>
                                                        Automatic Shipment Update Synchronization
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#637381' }}>
                                                        Toggle how and when real-time fulfillment data flows back into your stock records.
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Switch 
                                                checked={updateFlag}
                                                onChange={(e) => handleShipmentConfigChange(e, 'switch')}
                                                disabled={isComponentWorking}
                                                color="primary"
                                            />
                                        </Box>
                                        
                                        <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
                                        
                                        <Box sx={{ opacity: updateFlag ? 1 : 0.4, transition: 'opacity 0.2s', pl: 1 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: updateFlag ? '#374151' : '#919eab', display: 'block', mb: 1 }}>
                                                TRIGGER DATA WEBHOOK SYNC STRUCTURE WHEN AN ORDER IS:
                                            </Typography>
                                            <RadioGroup
                                                name="fishbowl-approval-shipment-group"
                                                value={onCreateFlag ? 'create_shipment' : 'manifest_close'}
                                                onChange={(e) => handleShipmentConfigChange(e, 'radio')}
                                                row
                                            >
                                                <FormControlLabel 
                                                    value="create_shipment" 
                                                    disabled={!updateFlag || isComponentWorking} 
                                                    control={<Radio size="small" />} 
                                                    label={<Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Create shipment (Instantaneous)</Typography>} 
                                                    sx={{ mr: 4 }}
                                                />
                                                <FormControlLabel 
                                                    value="manifest_close" 
                                                    disabled={!updateFlag || isComponentWorking} 
                                                    control={<Radio size="small" />} 
                                                    label={<Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Manifest close (End of Day)</Typography>} 
                                                />
                                            </RadioGroup>
                                        </Box>
                                    </CardContent>
                                </Card>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Button 
                                        variant="contained" 
                                        disableElevation
                                        onClick={handleVerifyApproval}
                                        disabled={isComponentWorking}
                                        sx={{ bgcolor: '#212b36', '&:hover': { bgcolor: '#161c24' }, textTransform: 'none', fontWeight: 700 }}
                                    >
                                        {isComponentWorking ? <CircularProgress size={20} color="inherit" /> : "Verify & Finalize Token Activation"}
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {activeStep === 4 && (
                            <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                                <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 64, mb: 2 }} />
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#161c24', mb: 1 }}>
                                    Integration Successfully Connected!
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#637381', maxWidth: '500px', mx: 'auto', mb: 3 }}>
                                    Authorized secure access token has been generated and validated dynamically. Automatic order tracking and sync structures are fully functional.
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {errorMsg && activeStep !== 3 && activeStep !== 4 && (
                        <Alert 
                            severity={
                                fishbowlConfigure?.hasErrors || 
                                errorMsg.toLowerCase().includes('failed') || 
                                errorMsg.toLowerCase().includes('error') ||
                                errorMsg.toLowerCase().includes('missing')
                                    ? "error" 
                                    : "success"
                            } 
                            sx={{ mb: 3, borderRadius: '8px' }}
                        >
                            {errorMsg}
                        </Alert>
                    )}
                    <Divider />

                    {/* Stepper Navigation Actions Bottom Panel */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                        <Button
                            variant="text"
                            disabled={activeStep === 0 || activeStep === 4 || isComponentWorking}
                            onClick={handleBack}
                            sx={{ color: '#637381', fontWeight: 700, textTransform: 'none' }}
                        >
                            Back
                        </Button>
                        
                        {activeStep < 3 && (
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={handleNext}
                                disabled={isComponentWorking || isCurrentStepInvalid()}
                                sx={{ 
                                    bgcolor: '#212b36', 
                                    '&:hover': { bgcolor: '#161c24' }, 
                                    textTransform: 'none', 
                                    fontWeight: 700,
                                    px: 4,
                                    borderRadius: '6px'
                                }}
                            >
                                {isComponentWorking ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    activeStep === 2 ? 'Authorize Connection Pipeline' : 'Continue'
                                )}
                            </Button>
                        )}
                    </Box>

                </CardContent>
            </Card>
        </Box>
    );
};

export default FishbowlIntegration;